import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { InlineNotification } from '@carbon/react';

import { useBlobDownload } from 'lib/hooks/useBlobDownload';
import type { TableState } from 'components/data-table/types';

import {
  draftAssetChangePendingAtom,
  draftReloadAtom,
  draftTablesReloadAtom,
  draftSlotsQueryLoadableAtom,
  focusedSlotIdAtom,
  slotsSelectionDisabledAtom,
  togglePinAtom,
  updateSlotSelectionAtom,
  updateSlotSelectionLoadableAtom,
} from '../atoms';
import { usePackageWizardCtx } from '../context';
import { useDraftAssetChange } from '../draft-build-hooks';
import { SlotTable } from '../slot-table';
import type {
  DraftSlotsResponse,
  FlatSlotRow,
  SlotSelectionState,
} from '../types';
import {
  buildSelectionFilters,
  draftSlotFilterStateToParams,
  firstTableState,
  flattenDraftSlots,
  markDraftTouched,
  sortParamsFromState,
} from '../utils';
import styles from './slots-table.module.scss';

/** Props for {@link SlotsTable}. */
export interface SlotsTableProps {
  /** UUID from `POST /packages/drafts`. */
  draftId: string;
  /**
   * Per-row Edit handler (INS-9066). Forwarded to `<SlotTable>` so the
   * row-level overflow menu can dispatch into the parent's modal flow
   * with the row's assetType + assetId for pre-population. Page-level
   * routing (which modal to open) lives at the consumer.
   */
  onEditAsset?: (row: FlatSlotRow) => void;
  /**
   * Holds selection still while the parent rebuilds the draft (a scenario
   * switch), on top of the table's own in-flight gating.
   */
  disableSelection?: boolean;
  /** Disables the per-row Remove while the parent rebuilds the draft. */
  disableRemove?: boolean;
}

/**
 * Step 1 slot table — feature-local wrapper that owns the
 * `/queries/packages/drafts/:draft_id/slots` fetch, the BE-driven
 * selection mutations, and the refetch-on-success loop. Subscription /
 * error / loading / selection logic live here; rendering and verification
 * rollups live in `<SlotTable>` and its cells.
 *
 * Selection model is server-driven: each fetch's `meta.selectionState`
 * + per-row `tract.selected` is the truth. Click → mutate → refetch.
 *
 * @param {SlotsTableProps} props - Draft id
 * @return {JSX.Element} The Step 1 slot table
 */
export const SlotsTable: FC<SlotsTableProps> = ({
  draftId,
  onEditAsset,
  disableSelection = false,
  disableRemove = false,
}) => {
  const { form } = usePackageWizardCtx();
  const [queryState, fetchSlots] = useAtom(draftSlotsQueryLoadableAtom);
  const updateSelection = useSetAtom(updateSlotSelectionAtom);
  const updateState = useAtomValue(updateSlotSelectionLoadableAtom);
  const selectionDisabled = useAtomValue(slotsSelectionDisabledAtom);
  // An asset change still in flight or building: the server would refuse a
  // Remove or a selection change.
  const assetChangePending = useAtomValue(draftAssetChangePendingAtom);
  const focusedSlotId = useAtomValue(focusedSlotIdAtom);
  const togglePin = useSetAtom(togglePinAtom);
  const changeAssets = useDraftAssetChange(form);
  const reload = useAtomValue(draftReloadAtom);
  const tablesReload = useAtomValue(draftTablesReloadAtom);
  // Why the last Remove was refused, until the next one.
  const [removeError, setRemoveError] = useState<string | null>(null);

  // Hold the most recent TableState so refetch-after-mutation can
  // re-issue the same page/filter scope, and so toggle-all can build
  // the BE filter payload from the current view. State would force re-
  // renders on every page/search change without unblocking new behavior.
  const lastParamsRef = useRef<TableState | null>(null);

  /**
   * Fetch slots with the full `TableState` shape — page, page size,
   * search text, sort, and column filters — so the BE-rendered table
   * matches what `buildSelectionFilters` later sends as the bulk-toggle
   * scope. Also stashes the params for later refetch / toggle-all.
   *
   * @param {TableState} params - DataTable's load callback shape
   * @return {void}
   */
  const loadData = useCallback(
    (params: TableState) => {
      lastParamsRef.current = params;
      fetchSlots({
        draftId,
        page: params.page,
        pageSize: params.pageSize,
        text: params.globalFilter || undefined,
        ...sortParamsFromState(params.sorting),
        ...draftSlotFilterStateToParams(params.columnFilters),
        // Wizard map merges these row-level geometries with the
        // selected-only `maps/features` set; opt in here so they ride
        // the same fetch.
        includeGeometry: true,
      });
    },
    [draftId, fetchSlots]
  );

  /**
   * Re-issue the last fetch with the same params. Used after a mutation
   * (success or failure) so `tract.selected` and `meta.selectionState`
   * reflect the new BE truth and the table UI re-syncs.
   *
   * @return {void}
   */
  const refetch = useCallback(() => {
    if (lastParamsRef.current !== null) loadData(lastParamsRef.current);
  }, [loadData]);

  // DataTable doesn't self-fire `loadData` on mount in paginated mode —
  // kick off the initial fetch when the draft id changes. Default sort
  // is by Property ID ASC so the order is deterministic on first paint
  // (BE's `Query::Packages::DraftSlots#order_clause` already tie-breaks
  // on `slot_id, unit_name`). Each settled change to the draft
  // (`draftReloadAtom`, `draftTablesReloadAtom`: an asset ticked, a build
  // done, a new asset) re-reads the same page, sort and filters.
  useEffect(() => {
    loadData(lastParamsRef.current ?? firstTableState('slotId'));
  }, [draftId, reload, tablesReload, loadData]);

  // Stale-while-revalidate: hold the last successful response so a
  // post-mutation refetch keeps the rows visible. DataTable renders its
  // refetch progress bar over the (dimmed) stale rows instead of unmounting
  // into a skeleton — preserving scroll position. The skeleton only shows on
  // the initial load, when there's no cached response yet.
  const lastDataRef = useRef<DraftSlotsResponse | null>(null);
  if (queryState.state === 'hasData') {
    lastDataRef.current = queryState.data;
  }
  const data =
    queryState.state === 'hasData' ? queryState.data : lastDataRef.current;
  const isLoading = queryState.state === 'loading';

  const rows = useMemo<FlatSlotRow[]>(
    () => (data ? flattenDraftSlots(data.slots) : []),
    [data]
  );
  const totalCount = data?.meta.totalCount;
  const selectedAllocationCount = data?.meta.selectedAllocationCount ?? 0;
  const selectionState: SlotSelectionState =
    data?.meta.selectionState ?? 'none';

  // Query error takes precedence — a failed fetch leaves no table to
  // interact with. Mutation error comes straight off the loadable;
  // it's auto-cleared by the atom on the next mutation attempt.
  const queryError =
    queryState.state === 'hasError'
      ? queryState.error instanceof Error
        ? queryState.error.message
        : 'Failed to fetch properties'
      : null;
  const mutationError =
    updateState.state === 'hasError'
      ? updateState.error instanceof Error
        ? updateState.error.message
        : 'Failed to update property selection'
      : null;
  const errorMessage = queryError ?? mutationError ?? removeError;

  /**
   * Single-allocation toggle. Routes the row's tuple into either
   * `selectedAllocations` or `deselectedAllocations` depending on the new
   * state, then refetches (success or failure) so the UI re-syncs to BE
   * truth. The mutation atom carries the error state; we don't need to
   * catch it locally.
   *
   * @param {FlatSlotRow} row - The clicked row
   * @param {boolean} selected - The row's new selected state
   * @return {Promise<void>}
   */
  const handleToggleRow = useCallback(
    async (row: FlatSlotRow, selected: boolean) => {
      const tuple = {
        slotId: row.slotId,
        unitName: row.unitName,
        assetId: row.assetId,
      };
      try {
        await updateSelection({
          draftId,
          ...(selected
            ? { selectedAllocations: [tuple] }
            : { deselectedAllocations: [tuple] }),
        });
        markDraftTouched(form);
      } catch {
        // Error state lives on the loadable — re-throwing would surface
        // as an unhandled rejection at the caller.
      } finally {
        refetch();
      }
    },
    [draftId, updateSelection, refetch, form]
  );

  /**
   * Header-click toggle. Sends `{ selectAll, filters? }` to flip every
   * allocation in the current filtered scope across all pages — the BE
   * walks the same filter expression `GET /slots` uses, so visible
   * filters in the table match exactly what the mutation flips.
   *
   * @param {boolean} selected - True for select-all, false for deselect-all
   * @return {Promise<void>}
   */
  const handleToggleAll = useCallback(
    async (selected: boolean) => {
      const filters = buildSelectionFilters(lastParamsRef.current);
      try {
        await updateSelection(
          filters !== null
            ? { draftId, selectAll: selected, filters }
            : { draftId, selectAll: selected }
        );
        markDraftTouched(form);
      } catch {
        // Error state lives on the loadable.
      } finally {
        refetch();
      }
    },
    [draftId, updateSelection, refetch, form]
  );

  const handlePinToggle = useCallback(
    (slotId: string) => togglePin({ kind: 'slot', id: slotId }),
    [togglePin]
  );

  /**
   * Per-row Remove. Unticks the row's asset through the draft-assets
   * endpoint. Unticking a whole Tract, wellbore or LTD asset answers at once;
   * unticking a Tract held in part rebuilds it whole, and the step waits for
   * that build (`useDraftAssetChange`). The step re-reads both tables and the
   * map once it settles, refused or not; a refusal shows above the table.
   */
  const handleRemoveAsset = useCallback(
    async (row: FlatSlotRow) => {
      try {
        await changeAssets({ draftId, removedAssetIds: [row.assetId] });
        setRemoveError(null);
      } catch (error) {
        setRemoveError(
          error instanceof Error ? error.message : 'Failed to remove the asset'
        );
      }
    },
    [draftId, changeAssets]
  );

  // BE emits a `Content-Disposition` filename via `send_data`; the
  // hook honors it and only falls back to the supplied name on omit.
  const { triggerDownload: handleExport } = useBlobDownload({
    url: `/queries/packages/drafts/${draftId}/slots/export`,
    filename: `slots_${draftId}.csv`,
  });

  return (
    <div className={styles.container}>
      {errorMessage !== null && (
        <InlineNotification
          kind="error"
          title="Properties table error"
          subtitle={errorMessage}
          hideCloseButton
          lowContrast
          className={styles.errorNotification}
        />
      )}
      <SlotTable
        rows={rows}
        totalCount={totalCount}
        isLoading={isLoading}
        loadData={loadData}
        initialTableState={{ sortColumn: 'slotId', sortDirection: 'asc' }}
        selectionState={selectionState}
        onToggleRow={handleToggleRow}
        onToggleAll={handleToggleAll}
        disableSelection={
          disableSelection || selectionDisabled || assetChangePending
        }
        pinnedSlotId={focusedSlotId}
        onPinToggle={handlePinToggle}
        headerCount={selectedAllocationCount}
        showSelectedSuffix
        onExport={handleExport}
        onEditAsset={onEditAsset}
        onRemoveAsset={handleRemoveAsset}
        disableRemove={disableRemove || assetChangePending}
      />
    </div>
  );
};
